/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🧠 RIGHTNAME.AI - INTELLIGENT INTENT VALIDATION SYSTEM
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * This system silently understands user intent from Q1-Q7 and detects mismatches
 * BEFORE expensive analysis runs. Generated using LLM-assisted design.
 * 
 * CORE PRINCIPLE:
 * - Never interrupt during data collection
 * - System understands silently in the background
 * - Only show validation at summary if mismatches exist
 * - User is always right - let them override
 * 
 * SUPPORTS: Hindi, English, Hinglish, Urdu, Tamil, Telugu brand names
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle, Edit3, Lightbulb } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// 🔤 TOKEN DICTIONARY - Multi-language Brand Name Understanding
// ═══════════════════════════════════════════════════════════════════════════════
// Each token maps to: meaning, suggested industry, confidence score

const TOKEN_DICTIONARY = {
  // ═══════════════════════════════════════════════════════════════════════════
  // 🍔 FOOD & BEVERAGE TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Tea related (Hindi/English/Urdu)
  chai: { meaning: 'tea', industry: 'Food & Beverage', category: 'Tea/Cafe', confidence: 95 },
  chaay: { meaning: 'tea', industry: 'Food & Beverage', category: 'Tea/Cafe', confidence: 95 },
  tea: { meaning: 'tea', industry: 'Food & Beverage', category: 'Tea/Cafe', confidence: 90 },
  karak: { meaning: 'strong tea', industry: 'Food & Beverage', category: 'Tea/Cafe', confidence: 90 },
  cutting: { meaning: 'half tea', industry: 'Food & Beverage', category: 'Tea/Cafe', confidence: 85 },
  
  // Coffee related
  coffee: { meaning: 'coffee', industry: 'Food & Beverage', category: 'Coffee/Cafe', confidence: 90 },
  kaapi: { meaning: 'coffee (South Indian)', industry: 'Food & Beverage', category: 'Coffee/Cafe', confidence: 90 },
  kopi: { meaning: 'coffee', industry: 'Food & Beverage', category: 'Coffee/Cafe', confidence: 85 },
  espresso: { meaning: 'coffee', industry: 'Food & Beverage', category: 'Coffee/Cafe', confidence: 90 },
  cappuccino: { meaning: 'coffee', industry: 'Food & Beverage', category: 'Coffee/Cafe', confidence: 90 },
  brew: { meaning: 'coffee/beer', industry: 'Food & Beverage', category: 'Cafe/Brewery', confidence: 75 },
  
  // Indian food
  biryani: { meaning: 'biryani rice dish', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  biriyani: { meaning: 'biryani', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  pulao: { meaning: 'rice dish', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  dosa: { meaning: 'South Indian crepe', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  dosai: { meaning: 'dosa', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  idli: { meaning: 'South Indian steamed cake', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  idly: { meaning: 'idli', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  vada: { meaning: 'fried snack', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  sambar: { meaning: 'lentil soup', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  uttapam: { meaning: 'thick dosa', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  thali: { meaning: 'meal platter', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  
  // Bread/Roti
  roti: { meaning: 'Indian bread', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  chapati: { meaning: 'flatbread', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  paratha: { meaning: 'layered bread', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  naan: { meaning: 'leavened bread', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  kulcha: { meaning: 'stuffed bread', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  
  // Fast food
  pizza: { meaning: 'pizza', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 95 },
  burger: { meaning: 'burger', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 95 },
  momos: { meaning: 'dumplings', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 95 },
  momo: { meaning: 'dumplings', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 95 },
  rolls: { meaning: 'wraps', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 85 },
  wrap: { meaning: 'wraps', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 85 },
  fries: { meaning: 'fries', industry: 'Food & Beverage', category: 'Restaurant/Fast Food', confidence: 85 },
  
  // Sweets & Desserts
  mithai: { meaning: 'sweets', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 95 },
  halwa: { meaning: 'sweet dish', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 95 },
  ladoo: { meaning: 'sweet balls', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 95 },
  barfi: { meaning: 'milk sweet', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 95 },
  gulab: { meaning: 'rose (gulab jamun)', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 70 },
  jalebi: { meaning: 'crispy sweet', industry: 'Food & Beverage', category: 'Sweets/Bakery', confidence: 95 },
  kulfi: { meaning: 'Indian ice cream', industry: 'Food & Beverage', category: 'Dessert', confidence: 95 },
  icecream: { meaning: 'ice cream', industry: 'Food & Beverage', category: 'Dessert', confidence: 90 },
  cake: { meaning: 'cake', industry: 'Food & Beverage', category: 'Bakery', confidence: 85 },
  bakery: { meaning: 'bakery', industry: 'Food & Beverage', category: 'Bakery', confidence: 95 },
  
  // Beverages
  lassi: { meaning: 'yogurt drink', industry: 'Food & Beverage', category: 'Beverage', confidence: 95 },
  chaas: { meaning: 'buttermilk', industry: 'Food & Beverage', category: 'Beverage', confidence: 95 },
  sharbat: { meaning: 'sweet drink', industry: 'Food & Beverage', category: 'Beverage', confidence: 90 },
  juice: { meaning: 'juice', industry: 'Food & Beverage', category: 'Beverage', confidence: 85 },
  smoothie: { meaning: 'smoothie', industry: 'Food & Beverage', category: 'Beverage', confidence: 85 },
  shake: { meaning: 'milkshake', industry: 'Food & Beverage', category: 'Beverage', confidence: 80 },
  
  // Restaurant/Food service
  dhaba: { meaning: 'roadside eatery', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  cafe: { meaning: 'cafe', industry: 'Food & Beverage', category: 'Cafe', confidence: 90 },
  canteen: { meaning: 'cafeteria', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  restaurant: { meaning: 'restaurant', industry: 'Food & Beverage', category: 'Restaurant', confidence: 95 },
  kitchen: { meaning: 'kitchen', industry: 'Food & Beverage', category: 'Cloud Kitchen', confidence: 75 },
  rasoi: { meaning: 'kitchen (Hindi)', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  khana: { meaning: 'food (Hindi)', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  bhojan: { meaning: 'meal (Hindi)', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  nashta: { meaning: 'breakfast (Hindi)', industry: 'Food & Beverage', category: 'Restaurant', confidence: 80 },
  paan: { meaning: 'betel leaf', industry: 'Food & Beverage', category: 'Paan Shop', confidence: 95 },
  masala: { meaning: 'spice', industry: 'Food & Beverage', category: 'Spices/Food', confidence: 80 },
  spice: { meaning: 'spice', industry: 'Food & Beverage', category: 'Spices/Food', confidence: 80 },
  tandoor: { meaning: 'clay oven', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  
  // Non-veg
  chicken: { meaning: 'chicken', industry: 'Food & Beverage', category: 'Restaurant', confidence: 80 },
  mutton: { meaning: 'mutton', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  kebab: { meaning: 'grilled meat', industry: 'Food & Beverage', category: 'Restaurant', confidence: 90 },
  tikka: { meaning: 'grilled pieces', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  seafood: { meaning: 'seafood', industry: 'Food & Beverage', category: 'Restaurant', confidence: 85 },
  fish: { meaning: 'fish', industry: 'Food & Beverage', category: 'Restaurant', confidence: 75 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 💻 TECHNOLOGY TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  tech: { meaning: 'technology', industry: 'Technology & Software', category: 'Technology', confidence: 90 },
  tekno: { meaning: 'technology', industry: 'Technology & Software', category: 'Technology', confidence: 85 },
  app: { meaning: 'application', industry: 'Technology & Software', category: 'Mobile App', confidence: 90 },
  application: { meaning: 'application', industry: 'Technology & Software', category: 'Software', confidence: 85 },
  soft: { meaning: 'software', industry: 'Technology & Software', category: 'Software', confidence: 80 },
  software: { meaning: 'software', industry: 'Technology & Software', category: 'Software', confidence: 95 },
  code: { meaning: 'programming', industry: 'Technology & Software', category: 'Software', confidence: 85 },
  coding: { meaning: 'programming', industry: 'Technology & Software', category: 'Software', confidence: 85 },
  data: { meaning: 'data', industry: 'Technology & Software', category: 'Data/Analytics', confidence: 80 },
  analytics: { meaning: 'analytics', industry: 'Technology & Software', category: 'Data/Analytics', confidence: 90 },
  cloud: { meaning: 'cloud computing', industry: 'Technology & Software', category: 'Cloud Services', confidence: 85 },
  ai: { meaning: 'artificial intelligence', industry: 'Technology & Software', category: 'AI/ML', confidence: 90 },
  ml: { meaning: 'machine learning', industry: 'Technology & Software', category: 'AI/ML', confidence: 90 },
  bot: { meaning: 'automation/chatbot', industry: 'Technology & Software', category: 'Automation', confidence: 85 },
  automation: { meaning: 'automation', industry: 'Technology & Software', category: 'Automation', confidence: 85 },
  digital: { meaning: 'digital', industry: 'Technology & Software', category: 'Digital Services', confidence: 75 },
  digi: { meaning: 'digital', industry: 'Technology & Software', category: 'Digital Services', confidence: 75 },
  cyber: { meaning: 'cyber', industry: 'Technology & Software', category: 'Cybersecurity', confidence: 85 },
  net: { meaning: 'internet', industry: 'Technology & Software', category: 'Internet', confidence: 70 },
  web: { meaning: 'web', industry: 'Technology & Software', category: 'Web Services', confidence: 80 },
  saas: { meaning: 'software as service', industry: 'Technology & Software', category: 'SaaS', confidence: 95 },
  platform: { meaning: 'platform', industry: 'Technology & Software', category: 'Platform', confidence: 80 },
  fintech: { meaning: 'financial technology', industry: 'Technology & Software', category: 'Fintech', confidence: 95 },
  edtech: { meaning: 'education technology', industry: 'Technology & Software', category: 'EdTech', confidence: 95 },
  healthtech: { meaning: 'health technology', industry: 'Technology & Software', category: 'HealthTech', confidence: 95 },
  agritech: { meaning: 'agriculture technology', industry: 'Technology & Software', category: 'AgriTech', confidence: 95 },
  proptech: { meaning: 'property technology', industry: 'Technology & Software', category: 'PropTech', confidence: 95 },
  api: { meaning: 'API', industry: 'Technology & Software', category: 'Developer Tools', confidence: 90 },
  sdk: { meaning: 'SDK', industry: 'Technology & Software', category: 'Developer Tools', confidence: 90 },
  
  // SaaS/Tool keywords (CRITICAL for detecting tools, validators, generators)
  tool: { meaning: 'software tool', industry: 'Technology & Software', category: 'SaaS/Tool', confidence: 90 },
  tools: { meaning: 'software tools', industry: 'Technology & Software', category: 'SaaS/Tool', confidence: 90 },
  evaluation: { meaning: 'evaluation/analysis', industry: 'Technology & Software', category: 'Analytics Tool', confidence: 90 },
  evaluator: { meaning: 'evaluation tool', industry: 'Technology & Software', category: 'Analytics Tool', confidence: 95 },
  analyzer: { meaning: 'analysis tool', industry: 'Technology & Software', category: 'Analytics Tool', confidence: 95 },
  analysis: { meaning: 'analysis', industry: 'Technology & Software', category: 'Analytics', confidence: 85 },
  checker: { meaning: 'checking tool', industry: 'Technology & Software', category: 'Utility Tool', confidence: 90 },
  validator: { meaning: 'validation tool', industry: 'Technology & Software', category: 'Utility Tool', confidence: 90 },
  generator: { meaning: 'generator tool', industry: 'Technology & Software', category: 'Generator Tool', confidence: 90 },
  builder: { meaning: 'builder tool', industry: 'Technology & Software', category: 'Builder Tool', confidence: 85 },
  maker: { meaning: 'maker tool', industry: 'Technology & Software', category: 'Creator Tool', confidence: 80 },
  creator: { meaning: 'creator tool', industry: 'Technology & Software', category: 'Creator Tool', confidence: 80 },
  scanner: { meaning: 'scanner tool', industry: 'Technology & Software', category: 'Utility Tool', confidence: 85 },
  tracker: { meaning: 'tracking tool', industry: 'Technology & Software', category: 'Tracking Tool', confidence: 85 },
  monitor: { meaning: 'monitoring tool', industry: 'Technology & Software', category: 'Monitoring Tool', confidence: 85 },
  dashboard: { meaning: 'dashboard', industry: 'Technology & Software', category: 'Analytics/Dashboard', confidence: 90 },
  automation: { meaning: 'automation', industry: 'Technology & Software', category: 'Automation Tool', confidence: 90 },
  scraper: { meaning: 'data scraper', industry: 'Technology & Software', category: 'Data Tool', confidence: 85 },
  converter: { meaning: 'converter tool', industry: 'Technology & Software', category: 'Utility Tool', confidence: 85 },
  optimizer: { meaning: 'optimization tool', industry: 'Technology & Software', category: 'Optimization Tool', confidence: 90 },
  scheduler: { meaning: 'scheduling tool', industry: 'Technology & Software', category: 'Productivity Tool', confidence: 85 },
  manager: { meaning: 'management tool', industry: 'Technology & Software', category: 'Management Tool', confidence: 75 },
  crm: { meaning: 'CRM software', industry: 'Technology & Software', category: 'CRM', confidence: 95 },
  erp: { meaning: 'ERP software', industry: 'Technology & Software', category: 'ERP', confidence: 95 },
  hrms: { meaning: 'HR software', industry: 'Technology & Software', category: 'HR Software', confidence: 95 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 👗 FASHION & APPAREL TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  wear: { meaning: 'clothing', industry: 'Fashion & Apparel', category: 'Clothing', confidence: 85 },
  vastra: { meaning: 'clothing (Hindi)', industry: 'Fashion & Apparel', category: 'Clothing', confidence: 90 },
  fashion: { meaning: 'fashion', industry: 'Fashion & Apparel', category: 'Fashion', confidence: 95 },
  style: { meaning: 'style', industry: 'Fashion & Apparel', category: 'Fashion', confidence: 80 },
  vogue: { meaning: 'fashion', industry: 'Fashion & Apparel', category: 'Fashion', confidence: 90 },
  couture: { meaning: 'high fashion', industry: 'Fashion & Apparel', category: 'Fashion', confidence: 95 },
  drape: { meaning: 'fabric draping', industry: 'Fashion & Apparel', category: 'Clothing', confidence: 85 },
  kapda: { meaning: 'cloth (Hindi)', industry: 'Fashion & Apparel', category: 'Clothing', confidence: 90 },
  silk: { meaning: 'silk fabric', industry: 'Fashion & Apparel', category: 'Fabric/Clothing', confidence: 85 },
  reshmi: { meaning: 'silk (Hindi)', industry: 'Fashion & Apparel', category: 'Fabric/Clothing', confidence: 90 },
  cotton: { meaning: 'cotton', industry: 'Fashion & Apparel', category: 'Fabric/Clothing', confidence: 80 },
  suti: { meaning: 'cotton (Hindi)', industry: 'Fashion & Apparel', category: 'Fabric/Clothing', confidence: 85 },
  ethnic: { meaning: 'traditional wear', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 90 },
  saree: { meaning: 'saree', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 95 },
  sari: { meaning: 'saree', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 95 },
  kurta: { meaning: 'kurta', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 95 },
  lehenga: { meaning: 'lehenga', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 95 },
  sherwani: { meaning: 'sherwani', industry: 'Fashion & Apparel', category: 'Ethnic Wear', confidence: 95 },
  designer: { meaning: 'designer', industry: 'Fashion & Apparel', category: 'Designer Wear', confidence: 85 },
  shoe: { meaning: 'footwear', industry: 'Fashion & Apparel', category: 'Footwear', confidence: 90 },
  footwear: { meaning: 'footwear', industry: 'Fashion & Apparel', category: 'Footwear', confidence: 95 },
  joota: { meaning: 'shoe (Hindi)', industry: 'Fashion & Apparel', category: 'Footwear', confidence: 90 },
  chappal: { meaning: 'sandal (Hindi)', industry: 'Fashion & Apparel', category: 'Footwear', confidence: 90 },
  bag: { meaning: 'bag', industry: 'Fashion & Apparel', category: 'Accessories', confidence: 80 },
  purse: { meaning: 'purse', industry: 'Fashion & Apparel', category: 'Accessories', confidence: 85 },
  accessory: { meaning: 'accessory', industry: 'Fashion & Apparel', category: 'Accessories', confidence: 85 },
  jewel: { meaning: 'jewelry', industry: 'Fashion & Apparel', category: 'Jewelry', confidence: 90 },
  jewelry: { meaning: 'jewelry', industry: 'Fashion & Apparel', category: 'Jewelry', confidence: 95 },
  ornament: { meaning: 'ornament', industry: 'Fashion & Apparel', category: 'Jewelry', confidence: 85 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 💄 BEAUTY & COSMETICS TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  glow: { meaning: 'radiance', industry: 'Beauty & Cosmetics', category: 'Skincare', confidence: 85 },
  chamak: { meaning: 'shine (Hindi)', industry: 'Beauty & Cosmetics', category: 'Skincare', confidence: 85 },
  skin: { meaning: 'skin', industry: 'Beauty & Cosmetics', category: 'Skincare', confidence: 85 },
  twacha: { meaning: 'skin (Hindi)', industry: 'Beauty & Cosmetics', category: 'Skincare', confidence: 90 },
  beauty: { meaning: 'beauty', industry: 'Beauty & Cosmetics', category: 'Beauty', confidence: 95 },
  sundarta: { meaning: 'beauty (Hindi)', industry: 'Beauty & Cosmetics', category: 'Beauty', confidence: 90 },
  cosmetic: { meaning: 'cosmetics', industry: 'Beauty & Cosmetics', category: 'Cosmetics', confidence: 95 },
  makeup: { meaning: 'makeup', industry: 'Beauty & Cosmetics', category: 'Cosmetics', confidence: 95 },
  salon: { meaning: 'salon', industry: 'Beauty & Cosmetics', category: 'Salon', confidence: 95 },
  parlour: { meaning: 'beauty parlour', industry: 'Beauty & Cosmetics', category: 'Salon', confidence: 95 },
  parlor: { meaning: 'beauty parlor', industry: 'Beauty & Cosmetics', category: 'Salon', confidence: 95 },
  spa: { meaning: 'spa', industry: 'Beauty & Cosmetics', category: 'Spa/Wellness', confidence: 90 },
  wellness: { meaning: 'wellness', industry: 'Beauty & Cosmetics', category: 'Wellness', confidence: 80 },
  hair: { meaning: 'hair', industry: 'Beauty & Cosmetics', category: 'Hair Care', confidence: 85 },
  baal: { meaning: 'hair (Hindi)', industry: 'Beauty & Cosmetics', category: 'Hair Care', confidence: 90 },
  nail: { meaning: 'nail', industry: 'Beauty & Cosmetics', category: 'Nail Care', confidence: 85 },
  ayurvedic: { meaning: 'ayurvedic', industry: 'Beauty & Cosmetics', category: 'Natural/Ayurvedic', confidence: 85 },
  herbal: { meaning: 'herbal', industry: 'Beauty & Cosmetics', category: 'Natural/Herbal', confidence: 85 },
  organic: { meaning: 'organic', industry: 'Beauty & Cosmetics', category: 'Organic', confidence: 75 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🏥 HEALTHCARE & PHARMA TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  health: { meaning: 'health', industry: 'Healthcare & Pharma', category: 'Healthcare', confidence: 85 },
  sehat: { meaning: 'health (Hindi/Urdu)', industry: 'Healthcare & Pharma', category: 'Healthcare', confidence: 90 },
  swasthya: { meaning: 'health (Hindi)', industry: 'Healthcare & Pharma', category: 'Healthcare', confidence: 90 },
  med: { meaning: 'medical', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 85 },
  medical: { meaning: 'medical', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 95 },
  chikitsa: { meaning: 'treatment (Hindi)', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 90 },
  cure: { meaning: 'cure', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 85 },
  ilaj: { meaning: 'treatment (Hindi/Urdu)', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 90 },
  pharma: { meaning: 'pharmaceutical', industry: 'Healthcare & Pharma', category: 'Pharma', confidence: 95 },
  dawai: { meaning: 'medicine (Hindi)', industry: 'Healthcare & Pharma', category: 'Pharma', confidence: 95 },
  aushadhi: { meaning: 'medicine (Hindi)', industry: 'Healthcare & Pharma', category: 'Pharma', confidence: 90 },
  clinic: { meaning: 'clinic', industry: 'Healthcare & Pharma', category: 'Clinic', confidence: 95 },
  hospital: { meaning: 'hospital', industry: 'Healthcare & Pharma', category: 'Hospital', confidence: 95 },
  aspatal: { meaning: 'hospital (Hindi)', industry: 'Healthcare & Pharma', category: 'Hospital', confidence: 90 },
  doctor: { meaning: 'doctor', industry: 'Healthcare & Pharma', category: 'Medical', confidence: 90 },
  vaidya: { meaning: 'doctor (Hindi/Ayurvedic)', industry: 'Healthcare & Pharma', category: 'Ayurvedic', confidence: 90 },
  hakim: { meaning: 'doctor (Urdu/Unani)', industry: 'Healthcare & Pharma', category: 'Unani', confidence: 90 },
  dental: { meaning: 'dental', industry: 'Healthcare & Pharma', category: 'Dental', confidence: 95 },
  dant: { meaning: 'teeth (Hindi)', industry: 'Healthcare & Pharma', category: 'Dental', confidence: 90 },
  eye: { meaning: 'eye', industry: 'Healthcare & Pharma', category: 'Eye Care', confidence: 80 },
  aankh: { meaning: 'eye (Hindi)', industry: 'Healthcare & Pharma', category: 'Eye Care', confidence: 90 },
  netra: { meaning: 'eye (Hindi)', industry: 'Healthcare & Pharma', category: 'Eye Care', confidence: 90 },
  lab: { meaning: 'laboratory', industry: 'Healthcare & Pharma', category: 'Diagnostics', confidence: 85 },
  pathology: { meaning: 'pathology', industry: 'Healthcare & Pharma', category: 'Diagnostics', confidence: 95 },
  diagnostic: { meaning: 'diagnostic', industry: 'Healthcare & Pharma', category: 'Diagnostics', confidence: 95 },
  yoga: { meaning: 'yoga', industry: 'Healthcare & Pharma', category: 'Wellness/Yoga', confidence: 85 },
  ayush: { meaning: 'traditional medicine', industry: 'Healthcare & Pharma', category: 'AYUSH', confidence: 90 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 💰 FINANCE & BANKING TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  pay: { meaning: 'payment', industry: 'Finance & Banking', category: 'Payments', confidence: 80 },
  payment: { meaning: 'payment', industry: 'Finance & Banking', category: 'Payments', confidence: 90 },
  bhugtan: { meaning: 'payment (Hindi)', industry: 'Finance & Banking', category: 'Payments', confidence: 90 },
  money: { meaning: 'money', industry: 'Finance & Banking', category: 'Finance', confidence: 85 },
  paisa: { meaning: 'money (Hindi)', industry: 'Finance & Banking', category: 'Finance', confidence: 90 },
  dhan: { meaning: 'wealth (Hindi)', industry: 'Finance & Banking', category: 'Finance', confidence: 85 },
  bank: { meaning: 'bank', industry: 'Finance & Banking', category: 'Banking', confidence: 95 },
  banking: { meaning: 'banking', industry: 'Finance & Banking', category: 'Banking', confidence: 95 },
  loan: { meaning: 'loan', industry: 'Finance & Banking', category: 'Lending', confidence: 95 },
  karz: { meaning: 'loan (Hindi/Urdu)', industry: 'Finance & Banking', category: 'Lending', confidence: 90 },
  rin: { meaning: 'loan (Hindi)', industry: 'Finance & Banking', category: 'Lending', confidence: 90 },
  invest: { meaning: 'investment', industry: 'Finance & Banking', category: 'Investment', confidence: 90 },
  nivesh: { meaning: 'investment (Hindi)', industry: 'Finance & Banking', category: 'Investment', confidence: 90 },
  fund: { meaning: 'fund', industry: 'Finance & Banking', category: 'Investment', confidence: 85 },
  kosh: { meaning: 'fund (Hindi)', industry: 'Finance & Banking', category: 'Investment', confidence: 85 },
  insurance: { meaning: 'insurance', industry: 'Finance & Banking', category: 'Insurance', confidence: 95 },
  bima: { meaning: 'insurance (Hindi)', industry: 'Finance & Banking', category: 'Insurance', confidence: 95 },
  credit: { meaning: 'credit', industry: 'Finance & Banking', category: 'Banking/Lending', confidence: 85 },
  debit: { meaning: 'debit', industry: 'Finance & Banking', category: 'Banking', confidence: 85 },
  wallet: { meaning: 'digital wallet', industry: 'Finance & Banking', category: 'Fintech', confidence: 85 },
  upi: { meaning: 'UPI payments', industry: 'Finance & Banking', category: 'Payments', confidence: 95 },
  rupay: { meaning: 'RuPay', industry: 'Finance & Banking', category: 'Payments', confidence: 90 },
  capital: { meaning: 'capital', industry: 'Finance & Banking', category: 'Investment', confidence: 80 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 📚 EDUCATION & EDTECH TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  learn: { meaning: 'learning', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  sikho: { meaning: 'learn (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  seekh: { meaning: 'learn (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  padho: { meaning: 'study (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  edu: { meaning: 'education', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  shiksha: { meaning: 'education (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 95 },
  vidya: { meaning: 'knowledge (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 90 },
  gyan: { meaning: 'knowledge (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 85 },
  school: { meaning: 'school', industry: 'Education & EdTech', category: 'School', confidence: 95 },
  vidyalaya: { meaning: 'school (Hindi)', industry: 'Education & EdTech', category: 'School', confidence: 95 },
  pathshala: { meaning: 'school (Hindi)', industry: 'Education & EdTech', category: 'School', confidence: 95 },
  academy: { meaning: 'academy', industry: 'Education & EdTech', category: 'Academy', confidence: 90 },
  gurukul: { meaning: 'traditional school (Hindi)', industry: 'Education & EdTech', category: 'Academy', confidence: 90 },
  tutor: { meaning: 'tutoring', industry: 'Education & EdTech', category: 'Tutoring', confidence: 90 },
  tuition: { meaning: 'tutoring', industry: 'Education & EdTech', category: 'Tutoring', confidence: 90 },
  class: { meaning: 'class', industry: 'Education & EdTech', category: 'Education', confidence: 75 },
  kaksha: { meaning: 'class (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 85 },
  course: { meaning: 'course', industry: 'Education & EdTech', category: 'Online Course', confidence: 85 },
  paathyakram: { meaning: 'curriculum (Hindi)', industry: 'Education & EdTech', category: 'Education', confidence: 85 },
  exam: { meaning: 'examination', industry: 'Education & EdTech', category: 'Test Prep', confidence: 90 },
  pariksha: { meaning: 'exam (Hindi)', industry: 'Education & EdTech', category: 'Test Prep', confidence: 90 },
  test: { meaning: 'test', industry: 'Education & EdTech', category: 'Test Prep', confidence: 75 },
  coach: { meaning: 'coaching', industry: 'Education & EdTech', category: 'Coaching', confidence: 85 },
  coaching: { meaning: 'coaching', industry: 'Education & EdTech', category: 'Coaching', confidence: 95 },
  prep: { meaning: 'preparation', industry: 'Education & EdTech', category: 'Test Prep', confidence: 85 },
  preparation: { meaning: 'preparation', industry: 'Education & EdTech', category: 'Test Prep', confidence: 85 },
  mock: { meaning: 'mock test/interview', industry: 'Education & EdTech', category: 'Test Prep/Interview Prep', confidence: 90 },
  practice: { meaning: 'practice', industry: 'Education & EdTech', category: 'Learning', confidence: 75 },
  interview: { meaning: 'interview preparation', industry: 'Education & EdTech', category: 'Interview Prep', confidence: 85 },
  sakshatkaar: { meaning: 'interview (Hindi)', industry: 'Education & EdTech', category: 'Interview Prep', confidence: 90 },
  career: { meaning: 'career', industry: 'Education & EdTech', category: 'Career Services', confidence: 80 },
  placement: { meaning: 'placement', industry: 'Education & EdTech', category: 'Placement Services', confidence: 90 },
  naukri: { meaning: 'job (Hindi)', industry: 'Education & EdTech', category: 'Job/Career', confidence: 85 },
  skill: { meaning: 'skill', industry: 'Education & EdTech', category: 'Skill Development', confidence: 80 },
  hunar: { meaning: 'skill (Hindi)', industry: 'Education & EdTech', category: 'Skill Development', confidence: 85 },
  certificate: { meaning: 'certification', industry: 'Education & EdTech', category: 'Certification', confidence: 85 },
  upsc: { meaning: 'UPSC exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 95 },
  ias: { meaning: 'IAS exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 95 },
  jee: { meaning: 'JEE exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 95 },
  neet: { meaning: 'NEET exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 95 },
  cat: { meaning: 'CAT exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 90 },
  gate: { meaning: 'GATE exam', industry: 'Education & EdTech', category: 'Competitive Exams', confidence: 95 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🏠 REAL ESTATE TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  property: { meaning: 'property', industry: 'Real Estate & Property', category: 'Real Estate', confidence: 90 },
  sampatti: { meaning: 'property (Hindi)', industry: 'Real Estate & Property', category: 'Real Estate', confidence: 90 },
  realty: { meaning: 'real estate', industry: 'Real Estate & Property', category: 'Real Estate', confidence: 95 },
  estate: { meaning: 'estate', industry: 'Real Estate & Property', category: 'Real Estate', confidence: 85 },
  home: { meaning: 'home', industry: 'Real Estate & Property', category: 'Housing', confidence: 80 },
  ghar: { meaning: 'home (Hindi)', industry: 'Real Estate & Property', category: 'Housing', confidence: 90 },
  makan: { meaning: 'house (Hindi)', industry: 'Real Estate & Property', category: 'Housing', confidence: 90 },
  flat: { meaning: 'apartment', industry: 'Real Estate & Property', category: 'Housing', confidence: 85 },
  apartment: { meaning: 'apartment', industry: 'Real Estate & Property', category: 'Housing', confidence: 90 },
  plot: { meaning: 'land plot', industry: 'Real Estate & Property', category: 'Land', confidence: 90 },
  zameen: { meaning: 'land (Hindi/Urdu)', industry: 'Real Estate & Property', category: 'Land', confidence: 95 },
  bhoomi: { meaning: 'land (Hindi)', industry: 'Real Estate & Property', category: 'Land', confidence: 90 },
  builder: { meaning: 'builder', industry: 'Real Estate & Property', category: 'Construction', confidence: 90 },
  nirman: { meaning: 'construction (Hindi)', industry: 'Real Estate & Property', category: 'Construction', confidence: 90 },
  rent: { meaning: 'rental', industry: 'Real Estate & Property', category: 'Rental', confidence: 85 },
  kiraya: { meaning: 'rent (Hindi)', industry: 'Real Estate & Property', category: 'Rental', confidence: 90 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ✈️ TRAVEL & HOSPITALITY TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  travel: { meaning: 'travel', industry: 'Travel & Hospitality', category: 'Travel', confidence: 95 },
  yatra: { meaning: 'journey (Hindi)', industry: 'Travel & Hospitality', category: 'Travel', confidence: 95 },
  safar: { meaning: 'journey (Hindi/Urdu)', industry: 'Travel & Hospitality', category: 'Travel', confidence: 95 },
  tour: { meaning: 'tour', industry: 'Travel & Hospitality', category: 'Tourism', confidence: 90 },
  tourism: { meaning: 'tourism', industry: 'Travel & Hospitality', category: 'Tourism', confidence: 95 },
  ghumna: { meaning: 'roaming (Hindi)', industry: 'Travel & Hospitality', category: 'Travel', confidence: 85 },
  hotel: { meaning: 'hotel', industry: 'Travel & Hospitality', category: 'Hotel', confidence: 95 },
  resort: { meaning: 'resort', industry: 'Travel & Hospitality', category: 'Resort', confidence: 95 },
  stay: { meaning: 'accommodation', industry: 'Travel & Hospitality', category: 'Accommodation', confidence: 80 },
  flight: { meaning: 'flight', industry: 'Travel & Hospitality', category: 'Airlines', confidence: 90 },
  udaan: { meaning: 'flight (Hindi)', industry: 'Travel & Hospitality', category: 'Airlines', confidence: 90 },
  airline: { meaning: 'airline', industry: 'Travel & Hospitality', category: 'Airlines', confidence: 95 },
  train: { meaning: 'train', industry: 'Travel & Hospitality', category: 'Railways', confidence: 85 },
  rail: { meaning: 'railway', industry: 'Travel & Hospitality', category: 'Railways', confidence: 90 },
  cab: { meaning: 'cab', industry: 'Travel & Hospitality', category: 'Transport', confidence: 85 },
  taxi: { meaning: 'taxi', industry: 'Travel & Hospitality', category: 'Transport', confidence: 90 },
  auto: { meaning: 'auto rickshaw', industry: 'Travel & Hospitality', category: 'Transport', confidence: 80 },
  booking: { meaning: 'booking', industry: 'Travel & Hospitality', category: 'Booking', confidence: 75 },
  trip: { meaning: 'trip', industry: 'Travel & Hospitality', category: 'Travel', confidence: 85 },
  holiday: { meaning: 'holiday', industry: 'Travel & Hospitality', category: 'Travel', confidence: 85 },
  vacation: { meaning: 'vacation', industry: 'Travel & Hospitality', category: 'Travel', confidence: 85 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 👔 HR & RECRUITMENT TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  job: { meaning: 'job', industry: 'HR & Recruitment', category: 'Job Portal', confidence: 90 },
  jobs: { meaning: 'jobs', industry: 'HR & Recruitment', category: 'Job Portal', confidence: 90 },
  rojgar: { meaning: 'employment (Hindi)', industry: 'HR & Recruitment', category: 'Job Portal', confidence: 95 },
  hire: { meaning: 'hiring', industry: 'HR & Recruitment', category: 'Recruitment', confidence: 90 },
  bharti: { meaning: 'recruitment (Hindi)', industry: 'HR & Recruitment', category: 'Recruitment', confidence: 90 },
  recruit: { meaning: 'recruitment', industry: 'HR & Recruitment', category: 'Recruitment', confidence: 95 },
  recruitment: { meaning: 'recruitment', industry: 'HR & Recruitment', category: 'Recruitment', confidence: 95 },
  hr: { meaning: 'human resources', industry: 'HR & Recruitment', category: 'HR Services', confidence: 90 },
  talent: { meaning: 'talent', industry: 'HR & Recruitment', category: 'Talent Acquisition', confidence: 85 },
  pratibha: { meaning: 'talent (Hindi)', industry: 'HR & Recruitment', category: 'Talent Acquisition', confidence: 85 },
  resume: { meaning: 'resume', industry: 'HR & Recruitment', category: 'Career Services', confidence: 90 },
  cv: { meaning: 'CV', industry: 'HR & Recruitment', category: 'Career Services', confidence: 90 },
  workforce: { meaning: 'workforce', industry: 'HR & Recruitment', category: 'HR Services', confidence: 85 },
  staffing: { meaning: 'staffing', industry: 'HR & Recruitment', category: 'Staffing', confidence: 95 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🎬 ENTERTAINMENT & MEDIA TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  music: { meaning: 'music', industry: 'Entertainment & Media', category: 'Music', confidence: 90 },
  sangeet: { meaning: 'music (Hindi)', industry: 'Entertainment & Media', category: 'Music', confidence: 95 },
  gaana: { meaning: 'song (Hindi)', industry: 'Entertainment & Media', category: 'Music', confidence: 90 },
  video: { meaning: 'video', industry: 'Entertainment & Media', category: 'Video', confidence: 80 },
  film: { meaning: 'film', industry: 'Entertainment & Media', category: 'Film', confidence: 90 },
  cinema: { meaning: 'cinema', industry: 'Entertainment & Media', category: 'Film', confidence: 95 },
  movie: { meaning: 'movie', industry: 'Entertainment & Media', category: 'Film', confidence: 90 },
  news: { meaning: 'news', industry: 'Entertainment & Media', category: 'News', confidence: 90 },
  samachar: { meaning: 'news (Hindi)', industry: 'Entertainment & Media', category: 'News', confidence: 95 },
  khabar: { meaning: 'news (Hindi/Urdu)', industry: 'Entertainment & Media', category: 'News', confidence: 95 },
  media: { meaning: 'media', industry: 'Entertainment & Media', category: 'Media', confidence: 85 },
  game: { meaning: 'gaming', industry: 'Entertainment & Media', category: 'Gaming', confidence: 85 },
  gaming: { meaning: 'gaming', industry: 'Entertainment & Media', category: 'Gaming', confidence: 95 },
  khel: { meaning: 'game (Hindi)', industry: 'Entertainment & Media', category: 'Gaming/Sports', confidence: 80 },
  entertainment: { meaning: 'entertainment', industry: 'Entertainment & Media', category: 'Entertainment', confidence: 95 },
  manoranjan: { meaning: 'entertainment (Hindi)', industry: 'Entertainment & Media', category: 'Entertainment', confidence: 95 },
  stream: { meaning: 'streaming', industry: 'Entertainment & Media', category: 'Streaming', confidence: 85 },
  ott: { meaning: 'OTT platform', industry: 'Entertainment & Media', category: 'Streaming', confidence: 95 },
  podcast: { meaning: 'podcast', industry: 'Entertainment & Media', category: 'Podcast', confidence: 95 },
  social: { meaning: 'social media', industry: 'Entertainment & Media', category: 'Social Media', confidence: 75 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🚗 AUTOMOTIVE TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  car: { meaning: 'car', industry: 'Automotive', category: 'Automotive', confidence: 85 },
  gaadi: { meaning: 'vehicle (Hindi)', industry: 'Automotive', category: 'Automotive', confidence: 90 },
  bike: { meaning: 'motorcycle', industry: 'Automotive', category: 'Two-Wheeler', confidence: 85 },
  motor: { meaning: 'motor', industry: 'Automotive', category: 'Automotive', confidence: 80 },
  vehicle: { meaning: 'vehicle', industry: 'Automotive', category: 'Automotive', confidence: 90 },
  ev: { meaning: 'electric vehicle', industry: 'Automotive', category: 'EV', confidence: 90 },
  electric: { meaning: 'electric', industry: 'Automotive', category: 'EV', confidence: 70 },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🌾 AGRICULTURE TOKENS
  // ═══════════════════════════════════════════════════════════════════════════
  
  agri: { meaning: 'agriculture', industry: 'Agriculture', category: 'Agriculture', confidence: 95 },
  farm: { meaning: 'farming', industry: 'Agriculture', category: 'Farming', confidence: 90 },
  krishi: { meaning: 'agriculture (Hindi)', industry: 'Agriculture', category: 'Agriculture', confidence: 95 },
  kisan: { meaning: 'farmer (Hindi)', industry: 'Agriculture', category: 'Agriculture', confidence: 95 },
  seed: { meaning: 'seeds', industry: 'Agriculture', category: 'Seeds', confidence: 85 },
  beej: { meaning: 'seed (Hindi)', industry: 'Agriculture', category: 'Seeds', confidence: 90 },
  fertilizer: { meaning: 'fertilizer', industry: 'Agriculture', category: 'Agri Inputs', confidence: 90 },
  khad: { meaning: 'fertilizer (Hindi)', industry: 'Agriculture', category: 'Agri Inputs', confidence: 90 },
  harvest: { meaning: 'harvest', industry: 'Agriculture', category: 'Agriculture', confidence: 85 },
  crop: { meaning: 'crop', industry: 'Agriculture', category: 'Agriculture', confidence: 90 },
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🔍 SILENT UNDERSTANDING ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Analyzes brand name and extracts tokens with their meanings
 * Returns suggested industry based on token analysis
 */
function analyzeBrandName(brandName) {
  if (!brandName) return { tokens: [], suggestedIndustry: null, confidence: 0 };
  
  const tokens = [];
  const brandLower = brandName.toLowerCase();
  const words = brandLower.split(/[\s\-_\.]+/);
  
  // Check each word against dictionary
  for (const word of words) {
    // Direct match
    if (TOKEN_DICTIONARY[word]) {
      tokens.push({
        word,
        ...TOKEN_DICTIONARY[word]
      });
      continue;
    }
    
    // Partial match (word contains token or token contains word)
    for (const [token, data] of Object.entries(TOKEN_DICTIONARY)) {
      if (word.includes(token) && token.length >= 3) {
        tokens.push({
          word,
          matchedToken: token,
          ...data
        });
        break;
      }
    }
  }
  
  // Also check full brand name for compound tokens
  for (const [token, data] of Object.entries(TOKEN_DICTIONARY)) {
    if (brandLower.includes(token) && token.length >= 4) {
      const alreadyFound = tokens.some(t => t.word === token || t.matchedToken === token);
      if (!alreadyFound) {
        tokens.push({
          word: token,
          matchedToken: token,
          ...data,
          matchType: 'compound'
        });
      }
    }
  }
  
  // Determine suggested industry based on highest confidence tokens
  if (tokens.length === 0) {
    return { tokens: [], suggestedIndustry: null, confidence: 0 };
  }
  
  // Group by industry and sum confidence
  const industryScores = {};
  for (const token of tokens) {
    const industry = token.industry;
    if (!industryScores[industry]) {
      industryScores[industry] = { score: 0, tokens: [] };
    }
    industryScores[industry].score += token.confidence;
    industryScores[industry].tokens.push(token);
  }
  
  // Find highest scoring industry
  let bestIndustry = null;
  let bestScore = 0;
  for (const [industry, data] of Object.entries(industryScores)) {
    if (data.score > bestScore) {
      bestScore = data.score;
      bestIndustry = industry;
    }
  }
  
  return {
    tokens,
    suggestedIndustry: bestIndustry,
    suggestedCategory: tokens[0]?.category,
    confidence: Math.min(bestScore, 100),
    industryScores
  };
}

/**
 * Analyzes category text and extracts industry suggestions
 */
function analyzeCategory(category) {
  if (!category) return { suggestedIndustry: null, confidence: 0, tokens: [] };
  
  const tokens = [];
  const categoryLower = category.toLowerCase();
  
  // Check for tokens in category
  for (const [token, data] of Object.entries(TOKEN_DICTIONARY)) {
    if (categoryLower.includes(token) && token.length >= 3) {
      tokens.push({
        word: token,
        ...data
      });
    }
  }
  
  if (tokens.length === 0) {
    return { suggestedIndustry: null, confidence: 0, tokens: [] };
  }
  
  // Find most common industry
  const industryCounts = {};
  for (const token of tokens) {
    industryCounts[token.industry] = (industryCounts[token.industry] || 0) + token.confidence;
  }
  
  let bestIndustry = null;
  let bestScore = 0;
  for (const [industry, score] of Object.entries(industryCounts)) {
    if (score > bestScore) {
      bestScore = score;
      bestIndustry = industry;
    }
  }
  
  return {
    suggestedIndustry: bestIndustry,
    confidence: Math.min(bestScore, 100),
    tokens
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🚨 MISMATCH DETECTION ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detects all mismatches in user input
 * Returns array of mismatch objects with type, severity, message, and suggestions
 */
export function detectMismatches(formData) {
  const mismatches = [];
  
  const brandName = formData.brand_names || '';
  const industry = formData.industry || '';
  const category = formData.category || '';
  const productType = formData.product_type || '';
  const monetization = formData.monetization_model || '';
  
  // Run analysis
  const brandAnalysis = analyzeBrandName(brandName);
  const categoryAnalysis = analyzeCategory(category);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 1: Brand Name vs Industry Mismatch
  // ═══════════════════════════════════════════════════════════════════════════
  if (brandAnalysis.suggestedIndustry && industry) {
    const suggestedIndustry = brandAnalysis.suggestedIndustry;
    const selectedIndustry = industry;
    
    // Check if they match (case-insensitive partial match)
    const matches = selectedIndustry.toLowerCase().includes(suggestedIndustry.toLowerCase().split(' ')[0]) ||
                   suggestedIndustry.toLowerCase().includes(selectedIndustry.toLowerCase().split(' ')[0]);
    
    if (!matches && brandAnalysis.confidence >= 70) {
      const tokens = brandAnalysis.tokens.slice(0, 3).map(t => `"${t.word}" (${t.meaning})`).join(', ');
      mismatches.push({
        type: 'BRAND_INDUSTRY_MISMATCH',
        severity: 'high',
        title: 'Brand Name vs Industry Mismatch',
        icon: '🏷️',
        message: `Your brand name "${brandName}" contains ${tokens} which suggests **${suggestedIndustry}**, but you selected **${selectedIndustry}**.`,
        question: 'What industry is your brand actually in?',
        options: [
          { label: `${suggestedIndustry} (matches your brand name)`, value: 'suggested', action: 'change_industry', newValue: suggestedIndustry },
          { label: `${selectedIndustry} (I'm using creative naming)`, value: 'keep', action: 'keep' },
        ],
        field: 'industry',
        suggestedValue: suggestedIndustry
      });
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 2: Category vs Industry Mismatch (CRITICAL)
  // ═══════════════════════════════════════════════════════════════════════════
  if (categoryAnalysis.suggestedIndustry && industry) {
    const categoryIndustry = categoryAnalysis.suggestedIndustry;
    const selectedIndustry = industry;
    
    const matches = selectedIndustry.toLowerCase().includes(categoryIndustry.toLowerCase().split(' ')[0]) ||
                   categoryIndustry.toLowerCase().includes(selectedIndustry.toLowerCase().split(' ')[0]);
    
    if (!matches && categoryAnalysis.confidence >= 60) {
      mismatches.push({
        type: 'CATEGORY_INDUSTRY_MISMATCH',
        severity: 'high',
        title: '⚠️ Category vs Industry Mismatch',
        icon: '🚨',
        message: `Your category "${category}" is clearly a **${categoryIndustry}** business, but you selected **${selectedIndustry}** as your industry.`,
        question: 'This seems like a mistake. Which industry are you actually in?',
        options: [
          { label: `${categoryIndustry} (correct for ${category})`, value: 'suggested', action: 'change_industry', newValue: categoryIndustry },
          { label: `${selectedIndustry} (I have a unique business model)`, value: 'keep', action: 'keep' },
        ],
        field: 'industry',
        suggestedValue: categoryIndustry
      });
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 3: Brand Name vs Category Conflict (Chai + Coffee)
  // ═══════════════════════════════════════════════════════════════════════════
  if (brandAnalysis.tokens.length > 0 && category) {
    const categoryLower = category.toLowerCase();
    
    // Tea brand + Coffee category
    const teaTokens = brandAnalysis.tokens.filter(t => t.meaning?.includes('tea'));
    if (teaTokens.length > 0 && (categoryLower.includes('coffee') && !categoryLower.includes('tea'))) {
      mismatches.push({
        type: 'NAME_CATEGORY_CONFLICT',
        severity: 'high',
        title: 'Brand Name vs Category Conflict',
        icon: '☕',
        message: `Your brand name "${brandName}" contains "${teaTokens[0].word}" which means TEA, but your category is "${category}" (COFFEE).`,
        question: 'Are you building a tea business or coffee business?',
        options: [
          { label: 'Tea business (change category to Tea/Chai)', value: 'tea', action: 'change_category', newValue: 'Tea Cafe' },
          { label: 'Coffee business (creative naming with tea word)', value: 'coffee', action: 'keep' },
          { label: 'Both tea and coffee', value: 'both', action: 'change_category', newValue: 'Tea & Coffee Cafe' },
        ],
        field: 'category'
      });
    }
    
    // Coffee brand + Tea category
    const coffeeTokens = brandAnalysis.tokens.filter(t => t.meaning?.includes('coffee'));
    if (coffeeTokens.length > 0 && (categoryLower.includes('tea') || categoryLower.includes('chai')) && !categoryLower.includes('coffee')) {
      mismatches.push({
        type: 'NAME_CATEGORY_CONFLICT',
        severity: 'high',
        title: 'Brand Name vs Category Conflict',
        icon: '🍵',
        message: `Your brand name "${brandName}" contains "${coffeeTokens[0].word}" which means COFFEE, but your category is "${category}" (TEA).`,
        question: 'Are you building a coffee business or tea business?',
        options: [
          { label: 'Coffee business (change category)', value: 'coffee', action: 'change_category', newValue: 'Coffee Shop' },
          { label: 'Tea business (creative naming)', value: 'tea', action: 'keep' },
        ],
        field: 'category'
      });
    }
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 4: Category vs Product Type Mismatch
  // ═══════════════════════════════════════════════════════════════════════════
  const categoryLower = category.toLowerCase();
  
  // App/Software category but Physical product type
  if ((categoryLower.includes('app') || categoryLower.includes('software') || categoryLower.includes('platform') || categoryLower.includes('saas')) 
      && productType === 'Physical') {
    mismatches.push({
      type: 'CATEGORY_PRODUCT_MISMATCH',
      severity: 'high',
      title: 'Category vs Product Type Mismatch',
      icon: '📱',
      message: `Your category "${category}" is a digital product, but you selected "Physical" as product type.`,
      question: 'Is this a digital product or physical product?',
      options: [
        { label: 'Digital (app, software, platform)', value: 'digital', action: 'change_product_type', newValue: 'Digital' },
        { label: 'Physical (I\'m building physical products)', value: 'physical', action: 'keep' },
        { label: 'Hybrid (both digital and physical)', value: 'hybrid', action: 'change_product_type', newValue: 'Hybrid' },
      ],
      field: 'product_type',
      suggestedValue: 'Digital'
    });
  }
  
  // Restaurant/Cafe/Salon category but Digital product type
  if ((categoryLower.includes('restaurant') || categoryLower.includes('cafe') || categoryLower.includes('salon') || 
       categoryLower.includes('clinic') || categoryLower.includes('hotel') || categoryLower.includes('dhaba'))
      && productType === 'Digital' && !categoryLower.includes('app') && !categoryLower.includes('booking')) {
    mismatches.push({
      type: 'CATEGORY_PRODUCT_MISMATCH',
      severity: 'medium',
      title: 'Category vs Product Type Question',
      icon: '🏪',
      message: `Your category "${category}" is typically a physical business, but you selected "Digital" as product type.`,
      question: 'Are you building a physical establishment or a digital platform?',
      options: [
        { label: 'Physical establishment (restaurant, salon, etc.)', value: 'physical', action: 'change_product_type', newValue: 'Physical' },
        { label: 'Digital platform (booking app, aggregator)', value: 'digital', action: 'keep' },
        { label: 'Hybrid (physical + online presence)', value: 'hybrid', action: 'change_product_type', newValue: 'Hybrid' },
      ],
      field: 'product_type'
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 5: Monetization vs Product Type Mismatch
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Ad-supported for Physical product
  if (monetization === 'Ad-supported/Free' && productType === 'Physical') {
    mismatches.push({
      type: 'MONETIZATION_PRODUCT_MISMATCH',
      severity: 'high',
      title: 'Monetization Model Mismatch',
      icon: '💰',
      message: `"Ad-supported/Free" monetization doesn't work for physical products like "${category}".`,
      question: 'How will your business actually make money?',
      options: [
        { label: 'One-time Purchase (pay per product/service)', value: 'onetime', action: 'change_monetization', newValue: 'One-time Purchase' },
        { label: 'I\'m building a digital product (change to Digital)', value: 'digital', action: 'change_product_type', newValue: 'Digital' },
      ],
      field: 'monetization_model',
      suggestedValue: 'One-time Purchase'
    });
  }
  
  // Freemium for Physical product
  if (monetization === 'Freemium' && productType === 'Physical') {
    mismatches.push({
      type: 'MONETIZATION_PRODUCT_MISMATCH',
      severity: 'medium',
      title: 'Monetization Model Question',
      icon: '💳',
      message: `"Freemium" model is typically for digital products, not physical ones like "${category}".`,
      question: 'Is this a digital product with free tier?',
      options: [
        { label: 'Yes, it\'s digital (change product type)', value: 'digital', action: 'change_product_type', newValue: 'Digital' },
        { label: 'No, change to One-time Purchase', value: 'onetime', action: 'change_monetization', newValue: 'One-time Purchase' },
        { label: 'I have a unique model (keep)', value: 'keep', action: 'keep' },
      ],
      field: 'monetization_model'
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 6: Restaurant/Food + Ad-supported = Very Unusual
  // ═══════════════════════════════════════════════════════════════════════════
  if ((categoryLower.includes('restaurant') || categoryLower.includes('cafe') || categoryLower.includes('food') ||
       categoryLower.includes('biryani') || categoryLower.includes('pizza') || categoryLower.includes('dhaba'))
      && monetization === 'Ad-supported/Free') {
    mismatches.push({
      type: 'BUSINESS_MODEL_CONFLICT',
      severity: 'high',
      title: 'Unusual Business Model',
      icon: '🍽️',
      message: `"Ad-supported" monetization for a food business like "${category}" is very unusual. Restaurants make money from food sales.`,
      question: 'How will this business make money?',
      options: [
        { label: 'One-time Purchase (pay per order)', value: 'onetime', action: 'change_monetization', newValue: 'One-time Purchase' },
        { label: 'Subscription (meal plans)', value: 'subscription', action: 'change_monetization', newValue: 'Subscription (B2C)' },
        { label: 'I\'m building a food delivery APP', value: 'app', action: 'change_category', newValue: 'Food Delivery App' },
      ],
      field: 'monetization_model'
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 7: Education/Interview Prep in wrong industry
  // ═══════════════════════════════════════════════════════════════════════════
  const educationKeywords = ['mock', 'interview', 'coaching', 'tutor', 'exam', 'prep', 'learn', 'course', 'class', 'academy', 'school'];
  const hasEducationCategory = educationKeywords.some(kw => categoryLower.includes(kw));
  
  if (hasEducationCategory && industry && !industry.toLowerCase().includes('education') && !industry.toLowerCase().includes('edtech')) {
    mismatches.push({
      type: 'CATEGORY_INDUSTRY_MISMATCH',
      severity: 'high',
      title: '⚠️ Education Category in Wrong Industry',
      icon: '📚',
      message: `Your category "${category}" is an education/training business, but you selected "${industry}" as your industry.`,
      question: 'This appears to be an education business. Is that correct?',
      options: [
        { label: 'Education & EdTech (correct)', value: 'education', action: 'change_industry', newValue: 'Education & EdTech' },
        { label: `${industry} (I have a specific reason)`, value: 'keep', action: 'keep' },
      ],
      field: 'industry',
      suggestedValue: 'Education & EdTech'
    });
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RULE 8: Hindi name but only targeting non-Indian markets
  // ═══════════════════════════════════════════════════════════════════════════
  const hindiTokens = ['chai', 'dosa', 'biryani', 'roti', 'lassi', 'dhaba', 'mithai', 'paan', 'khana', 'ghar', 'dhan', 'vidya', 'shiksha', 'swasthya', 'yatra'];
  const hasHindiName = hindiTokens.some(t => brandName.toLowerCase().includes(t));
  const countries = (formData.countries || '').toLowerCase();
  const hasIndia = countries.includes('india');
  
  if (hasHindiName && countries && !hasIndia) {
    mismatches.push({
      type: 'NAME_MARKET_MISMATCH',
      severity: 'medium',
      title: 'Hindi Brand Name + Non-Indian Market',
      icon: '🌍',
      message: `Your brand name "${brandName}" contains Hindi words, but you're not targeting India.`,
      question: 'Is this intentional?',
      options: [
        { label: 'Add India to target markets', value: 'add_india', action: 'add_country', newValue: 'India' },
        { label: 'Targeting Indian diaspora abroad', value: 'diaspora', action: 'keep' },
        { label: 'Keep as is (creative naming)', value: 'keep', action: 'keep' },
      ],
      field: 'countries'
    });
  }
  
  return mismatches;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 SUMMARY GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generates a human-readable summary of what the user is building
 */
export function generateBusinessSummary(formData) {
  const brandName = formData.brand_names || 'Your brand';
  const industry = formData.industry || 'your industry';
  const category = formData.category || 'your category';
  const productType = formData.product_type || 'product';
  const positioning = formData.positioning || 'mid-range';
  const countries = formData.countries || 'your target market';
  
  // Determine article
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const article = vowels.includes(positioning.toLowerCase()[0]) ? 'an' : 'a';
  
  return `You're building "${brandName}", ${article} ${positioning.toLowerCase()} ${productType.toLowerCase()} ${category.toLowerCase()} for the ${countries} market.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 MISMATCH WARNING MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function MismatchWarningModal({ 
  isOpen, 
  onClose, 
  mismatches, 
  formData,
  onContinue, 
  onEdit,
  onApplyFix
}) {
  // Don't render if not open or no mismatches
  if (!isOpen || !mismatches || mismatches.length === 0) return null;
  
  const hasHighSeverity = mismatches.some(m => m.severity === 'high');
  const summary = generateBusinessSummary(formData || {});
  
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600 text-xl">
            <AlertTriangle className="w-6 h-6" />
            {hasHighSeverity ? 'Wait — Let\'s Make Sure We Understand' : 'Quick Clarification'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-5 mt-4">
              {/* Summary of what we understood */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Here's what we understood:
                </div>
                <p className="text-slate-600 italic">"{summary}"</p>
              </div>
              
              {/* Mismatches */}
              <div className="space-y-4">
                <p className="text-slate-600 font-medium">
                  We noticed {mismatches.length === 1 ? 'something' : 'a few things'} that might need clarification:
                </p>
                
                {mismatches.map((mismatch, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border-l-4 ${
                      mismatch.severity === 'high' 
                        ? 'bg-amber-50 border-amber-500' 
                        : 'bg-blue-50 border-blue-400'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm mb-2">
                      <span className="text-lg">{mismatch.icon || '⚠️'}</span>
                      {mismatch.title}
                    </div>
                    <div className="text-slate-600 text-sm mb-3" 
                         dangerouslySetInnerHTML={{ __html: mismatch.message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} 
                    />
                    
                    {mismatch.question && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-sm font-medium text-slate-700 mb-2">
                          💭 {mismatch.question}
                        </div>
                        {mismatch.options && (
                          <div className="space-y-2">
                            {mismatch.options.map((option, optIndex) => (
                              <button
                                key={optIndex}
                                onClick={() => {
                                  if (option.action === 'keep') {
                                    // User wants to keep current value
                                    onContinue?.();
                                  } else if (onApplyFix) {
                                    onApplyFix(mismatch.field, option.newValue);
                                  }
                                }}
                                className="w-full text-left px-3 py-2 rounded-md text-sm bg-white border border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-colors"
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-slate-500 italic">
                If everything above is intentional (creative naming, unique business model), you can continue as-is.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6 flex-col sm:flex-row gap-2">
          <AlertDialogCancel 
            onClick={onEdit}
            className="flex items-center justify-center gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Let Me Fix It
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onContinue}
            className="flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700"
          >
            <CheckCircle className="w-4 h-4" />
            Yes, Continue As-Is
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
